import { Table, Tag, Typography } from 'antd'
import styled from 'styled-components'

import { SWMSHazardsDisplayBlock } from '../../types/blueprint'

import {
  HidePrintA4,
  ShowPrintA4,
} from '@/components/shared-components/PrintMedia'
import { useLocale } from '@/context/localeContext'
import { transformHazardsForTable } from '@/modules/swms-builder/swmsTemplateHelper'

import { buildPassTheme } from 'src/styles/theme'

const Desktop = styled(ShowPrintA4)`
  white-space: pre-wrap;

  ${({ theme }) => theme.media.lg`
    display: block;
  `}
`

const Mobile = styled(HidePrintA4)`
  white-space: pre-wrap;

  ${({ theme }) => theme.media.lg`
    display: none;
  `}
`

const CustomTable = styled(Table)`
  .ant-table-thead > tr > th {
    background-color: ${({ theme }) => theme.colour.secondary._50};
  }
`

const Activity = styled.div`
  padding-bottom: 1rem;
  // for this to work, ensure no "display: flex" as parent
  page-break-inside: avoid;

  &:not(:first-child) {
    margin-top: 2.5rem;
  }

  &:not(:last-child) {
    border-bottom: 2px solid ${({ theme }) => theme.colour.secondary._50};
  }
`

const ActivityTitle = styled(Typography.Text)`
  font-size: 1.25rem;
  font-weight: 600;
  background-color: ${({ theme }) => theme.colour.neutral.white};
  display: block;
  z-index: 1;

  @media screen {
    position: sticky;
    top: 0;
  }
`

const Hazards = styled.div`
  padding-left: 1rem;
  padding-top: 1.5rem;
`

const Label = styled.span`
  display: block;
  text-transform: uppercase;
  font-size: 0.625rem;
  letter-spacing: 1px;
  color: ${({ theme }) => theme.colour.secondary._400};
`

const Hazard = styled.div`
  position: relative;
  padding-bottom: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  // for this to work, ensure no "display: flex" as parent
  page-break-inside: avoid;

  &:not(:first-child) {
    margin-top: 1.75rem;
  }

  &:not(:last-child) {
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0%;
      width: 50%;
      height: 1px;
      background-color: ${({ theme }) => theme.colour.secondary._50};
    }
  }
`

const List = styled.ul`
  padding-left: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

type Props = {
  block: SWMSHazardsDisplayBlock
}

const columns = (t) => [
  {
    title: t('swmsBuilder:labels.hazards.workActivity'),
    dataIndex: 'workActivity',
    onCell: (row) => ({ rowSpan: row.activityRowSpan }),
  },
  {
    title: t('swmsBuilder:labels.hazards.hazard'),
    dataIndex: 'hazard',
    onCell: (row) => ({ rowSpan: row.hazardRowSpan }),
  },
  {
    title: t('swmsBuilder:labels.hazards.control'),
    dataIndex: 'control',
  },
  {
    title: t('swmsBuilder:labels.hazards.responsible'),
    dataIndex: 'responsible',
    onCell: (row) => ({ rowSpan: row.hazardRowSpan }),
    render: (responsible: string[]) => responsible?.join(', '),
  },
]

export const SwmsHazardsDisplayBlock: React.FC<Props> = ({ block }) => {
  const { t } = useLocale()

  if (!block.value?.length) return <>N/A</>

  return (
    <>
      <Desktop>
        <CustomTable
          bordered
          rowKey="id"
          dataSource={transformHazardsForTable(block.value)}
          columns={columns(t)}
          pagination={false}
          size="small"
        />
      </Desktop>

      <Mobile>
        {block.value.map((activity) => (
          <Activity key={activity.description}>
            <ActivityTitle>{activity.description}</ActivityTitle>
            <Hazards>
              {activity?.hazards?.map((hazard) => (
                <Hazard key={hazard.description}>
                  <div>
                    <Label>{t('swmsBuilder:labels.hazards.hazard')}</Label>
                    <strong>{hazard.description}</strong>
                  </div>

                  <div>
                    <Label>{t('swmsBuilder:labels.hazards.responsible')}</Label>
                    {hazard?.responsible?.map((responsible) => (
                      <Tag key={responsible}>{responsible}</Tag>
                    ))}
                  </div>

                  <div>
                    <Label>{t('swmsBuilder:labels.hazards.controls')}</Label>
                    <List>
                      {hazard.controls.map((control) => (
                        <li
                          key={control}
                          color={buildPassTheme.colour.secondary._300}
                        >
                          {control}
                        </li>
                      ))}
                    </List>
                  </div>
                </Hazard>
              ))}
            </Hazards>
          </Activity>
        ))}
      </Mobile>
    </>
  )
}
